import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import axios from 'axios';
import { FileText, Eye, Calendar, User, Folder, FolderOpen, ChevronRight, Download, File } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminReviews = ({ user, onLogout }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRM, setSelectedRM] = useState(null);
  const [rmFolders, setRMFolders] = useState([]);
  const [activeTab, setActiveTab] = useState('reports');
  
  // Knowledge Centre states
  const [rmFiles, setRMFiles] = useState([]);
  const [selectedRMForFiles, setSelectedRMForFiles] = useState(null);
  const [filesLoading, setFilesLoading] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchReports();
  }, []);

  useEffect(() => {
    if (activeTab === 'files') {
      fetchRMFiles();
    }
  }, [activeTab]);

  const fetchRMFiles = async () => {
    setFilesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const filesRes = await axios.get(`${API}/admin/knowledge-centre/all-files`, config);
      setRMFiles(filesRes.data.rm_files || []);
    } catch (error) {
      console.error('Error fetching RM files:', error);
      toast.error('Failed to fetch RM files');
    } finally {
      setFilesLoading(false);
    }
  };

  const downloadFile = (fileUrl, fileName) => {
    window.open(fileUrl, '_blank');
  };

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const reportsRes = await axios.get(`${API}/admin/all-reports`, config);
      setReports(reportsRes.data);
      
      // Group reports by RM
      const rmGroups = {};
      reportsRes.data.forEach(report => {
        const rmId = report.rm_id || 'unassigned';
        const rmName = report.rm_name || 'Unassigned';
        
        if (!rmGroups[rmId]) {
          rmGroups[rmId] = {
            rm_id: rmId,
            rm_name: rmName,
            reports: [],
            total_reports: 0
          };
        }
        rmGroups[rmId].reports.push(report);
        rmGroups[rmId].total_reports++;
      });
      
      setRMFolders(Object.values(rmGroups));
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ugmonk-yellow"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
            RM Reports
          </h1>
          <p className="text-gray-600">View generated reports and shared knowledge centre files from all RMs</p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="reports" className="data-[state=active]:bg-white">
              <FileText className="w-4 h-4 mr-2" />
              Generated Reports
            </TabsTrigger>
            <TabsTrigger value="files" className="data-[state=active]:bg-white">
              <Folder className="w-4 h-4 mr-2" />
              Shared Files
            </TabsTrigger>
          </TabsList>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <FileText size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total Reports</p>
                <p className="text-2xl font-bold text-gray-900">{reports.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <User size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Total RMs</p>
                <p className="text-2xl font-bold text-gray-900">{rmFolders.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <FileText size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Sent</p>
                <p className="text-2xl font-bold text-gray-900">{reports.filter(r => r.status === 'sent').length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <Eye size={24} className="text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide">Viewed</p>
                <p className="text-2xl font-bold text-gray-900">{reports.filter(r => r.status === 'viewed').length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Two Column Layout: RM Folders + Reports */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* RM Folders - Left Column */}
          <Card className="border-gray-200 bg-white lg:col-span-1">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Folder className="w-5 h-5 text-ugmonk-dark" />
                RM Folders
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="space-y-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
                  ))}
                </div>
              ) : rmFolders.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Folder className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No reports found</p>
                </div>
              ) : (
                rmFolders.map((rmFolder) => (
                  <div
                    key={rmFolder.rm_id}
                    onClick={() => setSelectedRM(rmFolder)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedRM?.rm_id === rmFolder.rm_id
                        ? 'border-[#ffd87a] bg-[#ffd87a]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-[#fffdf0]">
                        {selectedRM?.rm_id === rmFolder.rm_id ? (
                          <FolderOpen className="w-5 h-5 text-ugmonk-dark" />
                        ) : (
                          <Folder className="w-5 h-5 text-ugmonk-dark" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900">{rmFolder.rm_name}</div>
                        <div className="text-sm text-gray-600">
                          {rmFolder.total_reports} report{rmFolder.total_reports !== 1 ? 's' : ''}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Reports Table - Right Column */}
          <Card className="border-gray-200 bg-white lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-ugmonk-dark" />
                {selectedRM ? `Reports by ${selectedRM.rm_name}` : 'All Reports'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedRM ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p className="text-lg font-medium mb-2">Select an RM folder</p>
                  <p className="text-sm">Click on any RM folder on the left to view their reports</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {selectedRM.reports.map((report) => (
                    <div
                      key={report.id}
                      className="p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-gray-900">{report.user_name}</h4>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              report.status === 'viewed' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                              report.status === 'sent' ? 'bg-[#fffdf0] text-ugmonk-dark' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {report.status.charAt(0).toUpperCase() + report.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-4 h-4" />
                              {new Date(report.created_at).toLocaleDateString('en-IN')}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => navigate(`/report/${report.id}`)}
                          className="flex items-center gap-1.5 px-3 py-2 text-sm bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black rounded-lg font-medium transition-colors"
                        >
                          <Eye size={16} />
                          View
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
          </TabsContent>

          {/* Shared Files Tab */}
          <TabsContent value="files" className="space-y-6">
            {/* Stats for Files */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-[#fffdf0]">
                    <File size={24} className="text-ugmonk-dark" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Total Shared Files</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {rmFiles.reduce((sum, rm) => sum + rm.file_count, 0)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-[#fffdf0]">
                    <User size={24} className="text-ugmonk-dark" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">RMs Sharing</p>
                    <p className="text-2xl font-bold text-gray-900">{rmFiles.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-ugmonk-border">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-lg bg-[#fffdf0]">
                    <Folder size={24} className="text-ugmonk-dark" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Non-Personal</p>
                    <p className="text-2xl font-bold text-gray-900">View Only</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Two Column Layout: RM Folders + Files */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* RM Folders - Left Column */}
              <Card className="border-gray-200 bg-white lg:col-span-1">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <Folder className="w-5 h-5 text-ugmonk-dark" />
                    RM Folders
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 max-h-[600px] overflow-y-auto">
                  {filesLoading ? (
                    <div className="space-y-2">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className="h-16 rounded-lg bg-gray-100 animate-pulse" />
                      ))}
                    </div>
                  ) : rmFiles.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Folder className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No shared files found</p>
                    </div>
                  ) : (
                    rmFiles.map((rmFile) => (
                      <div
                        key={rmFile.rm_id}
                        onClick={() => setSelectedRMForFiles(rmFile)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                          selectedRMForFiles?.rm_id === rmFile.rm_id
                            ? 'border-[#1a1a1a] bg-[#1a1a1a]/5'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-[#fffdf0]">
                            {selectedRMForFiles?.rm_id === rmFile.rm_id ? (
                              <FolderOpen className="w-5 h-5 text-ugmonk-dark" />
                            ) : (
                              <Folder className="w-5 h-5 text-ugmonk-dark" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{rmFile.rm_name}</div>
                            <div className="text-sm text-gray-600">
                              {rmFile.file_count} file{rmFile.file_count !== 1 ? 's' : ''}
                            </div>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
              </Card>

              {/* Files List - Right Column */}
              <Card className="border-gray-200 bg-white lg:col-span-2">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-900 flex items-center gap-2">
                    <File className="w-5 h-5 text-ugmonk-dark" />
                    {selectedRMForFiles ? `Files by ${selectedRMForFiles.rm_name}` : 'Shared Files'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedRMForFiles ? (
                    <div className="text-center py-12 text-gray-500">
                      <File className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p className="text-lg font-medium mb-2">Select an RM folder</p>
                      <p className="text-sm">Click on any RM folder on the left to view their shared files</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[600px] overflow-y-auto">
                      {selectedRMForFiles.files.map((file) => (
                        <div
                          key={file.id}
                          className="p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <File className="w-4 h-4 text-ugmonk-dark" />
                                <h4 className="font-semibold text-gray-900">{file.original_name || file.name}</h4>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                  {file.file_type.toUpperCase()}
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[#fffdf0] text-ugmonk-dark">
                                  Shared
                                </span>
                              </div>
                              {file.description && (
                                <p className="text-sm text-gray-600 mb-2">{file.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(file.created_at).toLocaleDateString('en-IN')}
                                </div>
                                <span>
                                  {(file.file_size / 1024).toFixed(2)} KB
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => downloadFile(file.cloud_url, file.original_name || file.name)}
                              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-[#1a1a1a] hover:bg-[#1a1a1a] text-white rounded-lg font-medium transition-colors"
                            >
                              <Download size={16} />
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default AdminReviews;
